import { useState, useEffect } from 'react';
import { useAuth } from '@/stores/auth';
import { authApi } from '@/lib/api';
import { useTranslation } from 'react-i18next';
import { Camera, Lock, Globe, Bell, Save, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  // Profile form state
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || '');
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Messages
  const [profileMessage, setProfileMessage] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setPhone(user.phone || '');
      setAvatarUrl(user.avatarUrl || '');
    }
  }, [user]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileMessage('');
    setProfileError('');

    try {
      const result = await authApi.updateProfile({ name, phone, avatarUrl });
      await refreshUser();
      setProfileMessage('Profile updated successfully!');
      setTimeout(() => setProfileMessage(''), 3000);
    } catch (error: any) {
      setProfileError(error.message || 'Failed to update profile');
      setTimeout(() => setProfileError(''), 3000);
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordMessage('');
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordLoading(false);
      setTimeout(() => setPasswordError(''), 3000);
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      setPasswordLoading(false);
      setTimeout(() => setPasswordError(''), 3000);
      return;
    }

    try {
      await authApi.changePassword({ currentPassword, newPassword });
      setPasswordMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordMessage(''), 3000);
    } catch (error: any) {
      setPasswordError(error.message || 'Failed to change password');
      setTimeout(() => setPasswordError(''), 3000);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{isRTL ? 'إعدادات الملف الشخصي' : 'Profile Settings'}</h1>
        <p className="text-muted-foreground">{isRTL ? 'إدارة إعدادات حسابك وتفضيلاتك' : 'Manage your account settings and preferences'}</p>
      </div>

      <div className="space-y-6">
        {/* {isRTL ? 'المعلومات الشخصية' : 'Personal Information'} */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {isRTL ? 'المعلومات الشخصية' : 'Personal Information'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              {/* Avatar */}
              <div className="flex items-center gap-6">
                <div className="relative">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Profile" className="w-24 h-24 rounded-full object-cover" />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-[#8B0000] flex items-center justify-center text-white text-3xl font-bold">
                      {name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="absolute bottom-0 right-0 rounded-full p-2"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex-1">
                  <Label htmlFor="avatarUrl">{isRTL ? 'رابط صورة الملف الشخصي' : 'Profile Picture URL'}</Label>
                  <Input
                    id="avatarUrl"
                    type="url"
                    value={avatarUrl}
                    onChange={(e) => setAvatarUrl(e.target.value)}
                    placeholder="https://example.com/avatar.jpg"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {isRTL ? 'أدخل رابط صورة أو اتركه فارغاً لاستخدام الأحرف الأولى' : 'Enter an image URL or leave blank to use initials'}
                  </p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">{isRTL ? 'الاسم الكامل' : 'Full Name'}</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{isRTL ? 'البريد الإلكتروني' : 'Email'}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">{isRTL ? 'لا يمكن تغيير البريد الإلكتروني' : 'Email cannot be changed'}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{isRTL ? 'رقم الهاتف' : 'Phone Number'}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => { const v = e.target.value.replace(/[^0-9+]/g, ''); setPhone(v); }}
                    placeholder="+964 7XX XXX XXXX"
                  />
                </div>
              </div>

              {profileMessage && (
                <div className="bg-green-50 text-green-800 p-3 rounded-md text-sm">
                  {profileMessage}
                </div>
              )}
              {profileError && (
                <div className="bg-red-50 text-red-800 p-3 rounded-md text-sm">
                  {profileError}
                </div>
              )}

              <Button type="submit" disabled={profileLoading} className="gap-2">
                <Save className="h-4 w-4" />
                {profileLoading ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : (isRTL ? 'حفظ التغييرات' : 'Save Changes')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* {isRTL ? 'تغيير كلمة المرور' : 'Change Password'} */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              {isRTL ? 'تغيير كلمة المرور' : 'Change Password'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">{isRTL ? 'كلمة المرور الحالية' : 'Current Password'}</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">{isRTL ? 'كلمة المرور الجديدة' : 'New Password'}</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <p className="text-xs text-muted-foreground">{isRTL ? '٦ أحرف على الأقل' : 'Minimum 6 characters'}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{isRTL ? 'تأكيد كلمة المرور الجديدة' : 'Confirm New Password'}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              {passwordMessage && (
                <div className="bg-green-50 text-green-800 p-3 rounded-md text-sm">
                  {passwordMessage}
                </div>
              )}
              {passwordError && (
                <div className="bg-red-50 text-red-800 p-3 rounded-md text-sm">
                  {passwordError}
                </div>
              )}

              <Button type="submit" disabled={passwordLoading} className="gap-2">
                <Lock className="h-4 w-4" />
                {passwordLoading ? (isRTL ? 'جاري التحديث...' : 'Updating...') : (isRTL ? 'تحديث كلمة المرور' : 'Update Password')}
              </Button>
            </form>
          </CardContent>
        </Card>


        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {isRTL ? 'إعدادات الإشعارات' : 'Notification Settings'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {isRTL ? 'إعدادات الإشعارات ستتوفر قريباً. ستتمكن من تخصيص إشعارات البريد الإلكتروني والتطبيق.' : "Notification preferences coming soon. You'll be able to customize email and in-app notifications."}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
